import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TextInput, SelectInput, YesNoInput, DateInput, InlineInput, InlineToggle } from "../components/fields";
import { Alert } from "../components/Toast";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { useDocumentGuard } from "../hooks/useDocumentGuard";
import { User, Megaphone, Building2, X, Plus, PieChart, CheckCircle2, AlertTriangle } from "lucide-react";
import type { useStepValidation } from "../hooks";
import { uuid } from "../types";
import type { Owner, AffiliatedGroup, IntakeFormData, OwnershipAssignment } from "../types";
import { ownerDisplayName } from "../utils/ownerDisplayName";
import { OWNER_REQUIRED_MESSAGE } from "../validation/rules";
import { COMMONLY_CONTROLLED, MAX_OWNERS, MAX_AFFILIATED_ENTITIES, MULTI_EMPLOYER_UNSUPPORTED_MSG, isUnsupportedMultiEmployer, PEO_MASTER_PLAN, isUnsupportedPeoMasterPlan, PRIMARY_ENTITY_ID, isAffiliatedListShown, isOwnershipExempt } from "../types/structureConstants";

interface StepStructureProps {
  validation: ReturnType<typeof useStepValidation>;
}

const addButtonClass = "w-full mt-2 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-500 hover:border-brand-green hover:text-brand-green hover:bg-brand-green-05 transition-colors cursor-pointer flex items-center justify-center gap-1";
const disabledAddButtonClass = "w-full mt-2 py-2.5 border-2 border-dashed border-gray-200 rounded-lg text-sm font-medium text-gray-300 cursor-not-allowed flex items-center justify-center gap-1";

/**
 * Header badge for ownership-exempt org types (Nonprofit/Other): marks the
 * Company Owners and Ownership Breakdown cards as optional without the weight
 * of a full alert banner.
 */
function OptionalForOrgTypePill({ orgType }: { orgType: string }) {
  return (
    <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 whitespace-nowrap">
      <CheckCircle2 size={13} aria-hidden />
      Optional for Organization Type: {orgType}
    </span>
  );
}

function RowHeader({ label, onRemove, canRemove, ariaLabel }: { label: React.ReactNode; onRemove: () => void; canRemove?: boolean; ariaLabel: string }) {
  const removable = canRemove ?? true;
  return (
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-semibold text-gray-600 inline-flex items-center gap-2">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        disabled={!removable}
        aria-label={ariaLabel}
        className={`transition-colors ${removable ? "text-gray-400 hover:text-danger cursor-pointer" : "text-gray-200 cursor-not-allowed"}`}
      >
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  );
}

const PEO_OPTIONS = [
  { value: "", label: "Select one..." },
  { value: PEO_MASTER_PLAN, label: "Yes — our group is a PEO master plan" },
  { value: "leaving", label: "Yes — but we are leaving the PEO" },
  { value: "no-medical", label: "Yes — but we do not receive medical benefits through the PEO" },
];

const MULTI_EMPLOYER_OPTIONS = [
  { value: "", label: "Select plan type..." },
  { value: COMMONLY_CONTROLLED, label: "Commonly controlled (IRS)" },
  { value: "Professional Employer Organization (PEO)", label: "PEO" },
  { value: "Multiple Employer Welfare Arrangement (MEWA)", label: "MEWA" },
  { value: "Church", label: "Church" },
  { value: "Employer Association", label: "Employer Association" },
  { value: "Taft Hartley Union", label: "Taft Hartley Union" },
];

const EMPTY_OWNER: Owner = { id: "", firstName: "", lastName: "", mi: "" };
const EMPTY_GROUP: AffiliatedGroup = { id: "", name: "", taxId: "", employees: null, covered: null };

const CBA_MIN_DATE = new Date(1900, 0, 1);
const DECIMAL_2_PLACES = /^\d*\.?\d{0,2}$/;

// ─── Owner roster ────────────────────────────────────────────────────────────

function initialsFor(o: Owner): string {
  const first = (o.firstName || "").trim()[0] || "";
  const last = (o.lastName || "").trim()[0] || "";
  return `${first}${last}`.toUpperCase() || "?";
}


interface OwnerRosterRowProps {
  index: number;
  owner: Owner;
  onUpdate: (data: Partial<Owner>) => void;
  onRemove: () => void;
  canRemove: boolean;
  touchAll: number;
}

function OwnerRosterRow({ index, owner, onUpdate, onRemove, canRemove, touchAll }: OwnerRosterRowProps) {
  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-3 row-enter">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-brand-green-10 text-brand-green flex items-center justify-center text-sm font-bold shrink-0" aria-hidden="true">
          {initialsFor(owner)}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-600">Company Owner {index + 1}</span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label={`Remove owner ${index + 1}`}
          className={`transition-colors ${canRemove ? "text-gray-400 hover:text-danger cursor-pointer" : "text-gray-200 cursor-not-allowed"}`}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-4 md:grid-cols-5 gap-3">
        <InlineInput
          label="Last Name"
          value={owner.lastName}
          onChange={(v) => onUpdate({ lastName: v })}
          error={!owner.lastName.trim() ? "Last Name is required" : null}
          touchAllCounter={touchAll}
          placeholder="Last"
          maxLength={40}
          required
          containerClassName="col-span-4 md:col-span-2"
        />
        <InlineInput
          label="First Name"
          value={owner.firstName}
          onChange={(v) => onUpdate({ firstName: v })}
          error={!owner.firstName.trim() ? "First Name is required" : null}
          touchAllCounter={touchAll}
          placeholder="First"
          maxLength={40}
          required
          containerClassName="col-span-3 md:col-span-2"
        />
        <InlineInput
          label="M.I."
          value={owner.mi}
          onChange={(v) => onUpdate({ mi: v })}
          placeholder="MI"
          maxLength={2}
          containerClassName="col-span-1"
        />
      </div>
    </div>
  );
}

// ─── Primary entity (Entity 1) card ──────────────────────────────────────────

interface PrimaryEntityCardProps {
  companyLegalName: string;
  federalTaxId: string;
  eligibleEmployees: number | null;
  onEligibleEmployeesChange: (v: number | null) => void;
  touchAll: number;
}

/**
 * Locked-fields card for the primary group's Affiliation_Group__c representation
 * on submit. Legal Name / EIN / Covered are all filled from Step 1 (Company
 * Information) and disabled here — the only editable input is the primary's
 * "Eligible Employees" head-count, which the intake form doesn't collect on
 * Step 1 (Step 1's Avg W-2 counts serve a different reporting purpose). Rendered
 * only when the affiliated-entity list is shown; on paths where no affiliated
 * entities exist, the primary's Affiliation_Group__c is not created and no
 * eligible-employees input is needed.
 */
function PrimaryEntityCard({
  companyLegalName,
  federalTaxId,
  eligibleEmployees,
  onEligibleEmployeesChange,
  touchAll,
}: PrimaryEntityCardProps) {
  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-600 inline-flex items-center gap-2">
          Entity 1
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider">
            Primary Entity
          </span>
        </span>
      </div>

      <InlineInput
        label="Entity Legal Name"
        value={companyLegalName}
        onChange={() => {}}
        disabled
        placeholder="Legal entity name"
        hint="Locked — matches your company legal name from the Company page."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <InlineInput
          label="Federal Tax ID (EIN)"
          value={federalTaxId}
          onChange={() => {}}
          disabled
          placeholder="XX-XXXXXXX"
          hint="Locked — matches your company federal tax ID."
        />
        <InlineInput
          label="No. of Eligible Employees"
          type="number"
          value={eligibleEmployees !== null ? String(eligibleEmployees) : ""}
          onChange={(v) => {
            if (v === "") { onEligibleEmployeesChange(null); return; }
            const n = Number(v);
            if (n < 0 || n > 9999) return;
            onEligibleEmployeesChange(Math.floor(n));
          }}
          error={eligibleEmployees === null ? "No. of Eligible Employees is required" : eligibleEmployees > 9999 ? "Maximum is 9999" : null}
          touchAllCounter={touchAll}
          placeholder="e.g. 50"
          max={9999}
          required
        />
      </div>

      <div className="mt-3">
        <InlineToggle
          label="Covered Under Optimyl Policy?"
          value={true}
          onChange={() => {}}
          disabled
          required
          hint="Locked — the primary entity is always covered under the Optimyl policy."
        />
      </div>
    </div>
  );
}

// ─── Affiliated entity row ───────────────────────────────────────────────────

interface AffiliatedGroupRowProps {
  index: number;
  group: AffiliatedGroup;
  onUpdate: (data: Partial<AffiliatedGroup>) => void;
  onRemove: () => void;
  touchAll: number;
  /** When true, "Covered Under Optimyl Policy?" is locked to No and non-interactive. */
  coveredLocked?: boolean;
  /** When false, the remove button is disabled — used to enforce the AC's "at least one
   *  entity in addition to the primary" rule by locking the last remaining affiliated
   *  row (otherwise removing it drops affiliatedGroups.length to 0, which fails
   *  validation with no visible on-row indication since the row itself is gone). */
  canRemove?: boolean;
}

function AffiliatedGroupRow({ index, group, onUpdate, onRemove, touchAll, coveredLocked, canRemove }: AffiliatedGroupRowProps) {
  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-3 row-enter">
      <RowHeader
        label={<>
          Entity {index + 2}
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider">
            Additional Entity
          </span>
        </>}
        onRemove={onRemove}
        canRemove={canRemove}
        ariaLabel={`Remove entity ${index + 2}`}
      />

      <InlineInput
        label="Entity Legal Name"
        value={group.name}
        onChange={(v) => onUpdate({ name: v })}
        error={!group.name.trim() ? "Entity Legal Name is required" : null}
        touchAllCounter={touchAll}
        placeholder="Legal entity name"
        maxLength={40}
        required
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <InlineInput
          label="Federal Tax ID (EIN)"
          value={group.taxId}
          onChange={(v) => {
            const digits = v.replace(/\D/g, "").slice(0, 9);
            const masked = digits.length <= 2 ? digits : `${digits.slice(0, 2)}-${digits.slice(2)}`;
            onUpdate({ taxId: masked });
          }}
          error={!group.taxId.trim() ? "Federal Tax ID is required" : !/^\d{2}-\d{7}$/.test(group.taxId) ? "Enter a valid EIN (XX-XXXXXXX)" : null}
          touchAllCounter={touchAll}
          placeholder="XX-XXXXXXX"
          maxLength={10}
          required
        />
        <InlineInput
          label="No. of Eligible Employees"
          type="number"
          value={group.employees !== null ? String(group.employees) : ""}
          onChange={(v) => {
            if (v === "") { onUpdate({ employees: null }); return; }
            const n = Number(v);
            if (n < 0 || n > 9999) return;
            onUpdate({ employees: Math.floor(n) });
          }}
          error={group.employees === null ? "No. of Eligible Employees is required" : group.employees > 9999 ? "Maximum is 9999" : null}
          touchAllCounter={touchAll}
          placeholder="e.g. 50"
          max={9999}
          required
        />
      </div>

      <div className="mt-3">
        <InlineToggle
          label="Covered Under Optimyl Policy?"
          value={coveredLocked ? false : group.covered}
          onChange={(v) => onUpdate({ covered: v })}
          required
          disabled={coveredLocked}
          error={!coveredLocked && group.covered === null ? "Please select Yes or No" : null}
          touchAllCounter={touchAll}
          hint={coveredLocked ? "The plan covers employees of only one employer, so affiliated entities cannot be covered." : undefined}
        />
      </div>
    </div>
  );
}

// ─── Ownership row (within Ownership Breakdown section) ───────────────────────

interface OwnershipRowProps {
  index: number;
  assignment: OwnershipAssignment;
  ownerOptions: { value: string; label: string }[];
  onUpdate: (patch: Partial<OwnershipAssignment>) => void;
  onRemove: () => void;
  touchAll: number;
  /** True when this row's owner is enrolling here AND on another currently-visible
   *  entity's row — a person can only be covered by one employer's plan. Surfaces
   *  the AC error inline on the row so the user can see which assignment to flip. */
  hasMultiEnrollConflict?: boolean;
}

function OwnershipRow({ index, assignment, ownerOptions, onUpdate, onRemove, touchAll, hasMultiEnrollConflict }: OwnershipRowProps) {
  const ownershipNum = Number(assignment.ownership) || 0;
  const [editText, setEditText] = useState<string | null>(null);
  const displayValue = editText ?? (assignment.ownership > 0 ? String(assignment.ownership) : "");
  const commitOwnership = useCallback(() => {
    if (editText === null) return;
    const n = Number(editText);
    const clamped = Number.isFinite(n) ? Math.min(Math.max(Math.round(n * 100) / 100, 0), 100) : 0;
    onUpdate({ ownership: clamped });
    setEditText(null);
  }, [editText, onUpdate]);

  const showW2Warning =
    ownershipNum > 0 && ownershipNum < 10
    && assignment.enrollingInBenefits === true
    && assignment.receivingW2Wages === false;

  // Only show "Owner is required" once the user has attempted to advance the step
  // (touchAll counter fires) — otherwise a freshly-added empty row would immediately
  // flash the error before the user has had a chance to pick from the dropdown.
  const ownerError = touchAll > 0 && !assignment.ownerId ? "Owner is required" : undefined;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-3 row-enter">
      <RowHeader label={`Owner ${index + 1}`} onRemove={onRemove} ariaLabel={`Remove ownership row ${index + 1}`} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
        <SelectInput
          label="Owner"
          value={assignment.ownerId}
          onChange={(v) => onUpdate({ ownerId: v })}
          options={ownerOptions}
          error={ownerError}
          placeholder="Select owner..."
          required
        />
        <InlineInput
          label="% Ownership"
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={(v) => {
            if (v === "" || DECIMAL_2_PLACES.test(v)) setEditText(v);
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text");
            if (!DECIMAL_2_PLACES.test(pasted)) e.preventDefault();
          }}
          onBlur={commitOwnership}
          error={ownershipNum <= 0 ? "Ownership % is required" : null}
          touchAllCounter={touchAll}
          placeholder="e.g. 60"
          min={0}
          max={100}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 items-start">
        <InlineToggle
          label="Is W-2 employee?"
          value={assignment.receivingW2Wages}
          onChange={(v) => onUpdate({ receivingW2Wages: v })}
          error={assignment.receivingW2Wages === null ? "Please select Yes or No" : null}
          touchAllCounter={touchAll}
          required
        />
        <InlineToggle
          label="Is enrolling?"
          value={assignment.enrollingInBenefits}
          onChange={(v) => onUpdate({ enrollingInBenefits: v })}
          error={assignment.enrollingInBenefits === null ? "Please select Yes or No" : null}
          touchAllCounter={touchAll}
          required
        />
      </div>

      {showW2Warning && (
        <Alert
          type="warning"
          className="mt-3"
          message={<><strong>IMPORTANT NOTE:</strong> In order to be eligible, you need to be receiving W-2 wages since you are under 10%.</>}
        />
      )}

      {hasMultiEnrollConflict && (
        <Alert
          type="error"
          className="mt-3"
          message="An owner cannot be enrolling under multiple entities."
        />
      )}
    </div>
  );
}

// ─── Per-company Ownership Breakdown section ─────────────────────────────────

interface OwnershipSectionProps {
  entityId: string;
  companyName: string;
  entityNumber: number;
  assignments: OwnershipAssignment[];
  owners: Owner[];
  onAddRow: (assignment: OwnershipAssignment) => void;
  onUpdateRow: (id: string, patch: Partial<OwnershipAssignment>) => void;
  onRemoveRow: (id: string) => void;
  touchAll: number;
  /** Set of ownerIds whose enrollingInBenefits=true assignments span more than one
   *  currently-visible entity — this section's row flags itself when its (owner,
   *  enrolling) pair contributes to the conflict. Computed at the parent so all
   *  sections share the same source of truth. */
  multiEnrollOwnerIds: Set<string>;
}

function OwnershipSection({
  entityId,
  companyName,
  entityNumber,
  assignments,
  owners,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
  touchAll,
  multiEnrollOwnerIds,
}: OwnershipSectionProps) {
  const total = Math.round(assignments.reduce((sum, a) => sum + (Number(a.ownership) || 0), 0) * 100) / 100;
  const isFull = total === 100;
  const isOver = total > 100;
  // Nonprofit/Other are exempt from the ownership requirement — mirror the Step 3
  // schema (isOwnershipExempt) so this section never renders a "required" error
  // the schema wouldn't enforce.
  const orgType = useFormStore((s) => s.data.orgType);
  const ownershipExempt = isOwnershipExempt({ orgType });

  const availableOwnerIdsPerRow = (currentAssignmentId: string): string[] => {
    const takenElsewhere = new Set(
      assignments.filter((a) => a.id !== currentAssignmentId && a.ownerId).map((a) => a.ownerId),
    );
    return owners.map((o) => o.id).filter((id) => !takenElsewhere.has(id));
  };

  const canAddAnother = assignments.length < owners.length;

  const barColor = isFull ? "bg-success" : isOver ? "bg-danger" : ownershipExempt ? "bg-gray-400" : "bg-warning";
  const statusColor = isFull ? "text-success" : isOver ? "text-danger" : ownershipExempt ? "text-gray-500" : "text-warning";
  // aria-hidden — status icon is decorative; the statusText next to it already conveys the state.
  const statusIcon = isFull ? <CheckCircle2 size={14} aria-hidden /> : isOver ? <AlertTriangle size={14} aria-hidden /> : null;
  // Exempt org types (Nonprofit/Other) aren't held to the 100% rule, so partial
  // totals render neutrally instead of as amber "remaining" nags.
  const statusText = isFull
    ? "Fully accounted"
    : isOver
      ? `Over-allocated by ${Math.round((total - 100) * 100) / 100}%`
      : ownershipExempt
        ? total > 0
          ? `${Math.round(total * 100) / 100}% allocated`
          : "No owners listed"
        : `${Math.round((100 - total) * 100) / 100}% remaining`;

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-4 last:mb-0">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs font-bold text-brand-navy uppercase tracking-wider">Entity {entityNumber}</div>
          <h3 className="text-base font-semibold text-brand-navy break-words">{companyName || "Primary group"}</h3>
        </div>
        <div className={`flex items-center gap-1.5 text-sm font-semibold ${statusColor}`}>
          {statusIcon}
          <span>{statusText}</span>
        </div>
      </div>

      {/* aria-hidden — the numeric percent + status text above already carry the semantic content. */}
      <div className="h-2 rounded-full overflow-hidden bg-gray-200 mb-4" aria-hidden>
        {total > 0 && (
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(total, 100)}%` }} />
        )}
      </div>

      {assignments.map((a, i) => {
        const availableIds = new Set(availableOwnerIdsPerRow(a.id));
        const ownerOptions = [
          { value: "", label: "Select owner..." },
          ...owners
            .filter((o) => availableIds.has(o.id) || o.id === a.ownerId)
            .map((o) => ({ value: o.id, label: ownerDisplayName(o, `Owner ${owners.findIndex((x) => x.id === o.id) + 1}`) })),
        ];
        return (
          <OwnershipRow
            key={a.id}
            index={i}
            assignment={a}
            ownerOptions={ownerOptions}
            onUpdate={(patch) => onUpdateRow(a.id, patch)}
            onRemove={() => onRemoveRow(a.id)}
            touchAll={touchAll}
            hasMultiEnrollConflict={a.enrollingInBenefits === true && !!a.ownerId && multiEnrollOwnerIds.has(a.ownerId)}
          />
        );
      })}

      {canAddAnother ? (
        <button
          type="button"
          onClick={() =>
            // Create an empty ownership row — the user must explicitly pick the owner
            // from the dropdown. Auto-picking was silently attributing ownership to
            // whichever owner happened to be first in the roster, producing
            // surprise-assignments the user never intentionally made.
            onAddRow({
              id: uuid(),
              ownerId: "",
              entityId,
              ownership: 0,
              receivingW2Wages: null,
              enrollingInBenefits: null,
            })
          }
          className={addButtonClass}
        >
          <Plus size={14} />
          Add owner to {companyName || "primary group"}
        </button>
      ) : owners.length === 0 ? (
        <button type="button" disabled className={disabledAddButtonClass}>
          Add owners to the Company Owners roster above first
        </button>
      ) : (
        <button type="button" disabled className={disabledAddButtonClass}>
          All owners on the roster are already listed for this company
        </button>
      )}

      {/* Per-entity missing-owners error — surfaces the schema-side
          `ownershipAssignments.<entityId>.total` = "At least one owner is required"
          rule on submit. Gated on touchAll so the error appears only after the
          user tries to Continue / navigate away, matching how per-row errors are
          revealed. Rendered per entity so a mixed list makes it clear which
          block(s) still need owners assigned. Suppressed for ownership-exempt
          org types (Nonprofit/Other) where the schema imposes no such rule. */}
      {touchAll > 0 && assignments.length === 0 && !ownershipExempt && (
        <Alert
          type="error"
          className="mt-3"
          message={OWNER_REQUIRED_MESSAGE}
        />
      )}
    </div>
  );
}

// ─── Main Step Component ─────────────────────────────────────────────────────

export function StepStructure({ validation }: StepStructureProps) {
  const globalTouchCounter = useUiStore((s) => s.validationTouchCounter);
  const [mountCounter] = useState(globalTouchCounter);
  const rowTouchAll = Math.max(0, globalTouchCounter - mountCounter);

  // Clear affiliated groups when multi-employer type changes away from "Commonly controlled"
  // (Yes path) or when isCommonlyControlledGroup flips to something other than "Yes" (No path).
  const multiEmployerType = useFormStore((s) => s.data.multiEmployerType);
  const isMultiEmployer = useFormStore((s) => s.data.isMultiEmployer);
  const isCommonlyControlledGroup = useFormStore((s) => s.data.isCommonlyControlledGroup);
  const prevAffiliatedShownRef = useRef<boolean>(false);
  useEffect(() => {
    const affiliatedShown = isAffiliatedListShown({ isMultiEmployer, multiEmployerType, isCommonlyControlledGroup });
    if (prevAffiliatedShownRef.current && !affiliatedShown) {
      const store = useFormStore.getState();
      const groups = store.data.affiliatedGroups;
      if (groups.length > 0) {
        // Cascade: drop any ownership assignments that reference the entities we're
        // about to remove. setField bypasses the store's per-item removeAffiliatedGroup
        // cascade, so without this the assignments become ghosts — invisible in the UI
        // but still counted by the unassigned-owner warning check.
        const removedIds = new Set(groups.map((g) => g.id));
        const remainingAssignments = store.data.ownershipAssignments.filter(
          (a) => !removedIds.has(a.entityId),
        );
        store.setField("affiliatedGroups", [] as AffiliatedGroup[]);
        if (remainingAssignments.length !== store.data.ownershipAssignments.length) {
          store.setField("ownershipAssignments", remainingAssignments);
        }
      }
      // Primary's eligibleEmployees only makes sense when the primary Entity 1 card
      // is shown. Clear it on path off so a stale value doesn't leak into a later
      // submit if the user re-flips the path.
      if (store.data.primaryEligibleEmployees !== null) {
        store.setField("primaryEligibleEmployees", null);
      }
    }
    // Path activation: seed one blank affiliated entity so the user always sees an
    // Entity 2 card ready to fill in (the AC requires at least one entity whenever
    // the affiliated list is shown; showing the card immediately makes that
    // requirement visible instead of hiding it behind a "+ Add" click).
    if (!prevAffiliatedShownRef.current && affiliatedShown) {
      const store = useFormStore.getState();
      if (store.data.affiliatedGroups.length === 0) {
        store.setField("affiliatedGroups", [{
          id: uuid(),
          name: "",
          taxId: "",
          employees: null,
          covered: isMultiEmployer === "No" ? false : null,
        }] as AffiliatedGroup[]);
      }
    }
    prevAffiliatedShownRef.current = affiliatedShown;
  }, [isMultiEmployer, multiEmployerType, isCommonlyControlledGroup]);

  const cbaMaxDate = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear() + 5, d.getMonth(), d.getDate());
  }, []);

  const owners = useFormStore((s) => s.data.owners);
  // Nonprofit/Other are ownership-exempt (Mike Bentz, 2026-08-12): the roster may go to
  // zero rows, so the last row's remove button must not be locked for them.
  const stepOrgType = useFormStore((s) => s.data.orgType);
  const ownershipExemptStep = isOwnershipExempt({ orgType: stepOrgType });
  const hasCba = useFormStore((s) => s.data.hasCba);
  const hasPeo = useFormStore((s) => s.data.hasPeo);
  const peoType = useFormStore((s) => s.data.peoType);
  const affiliatedGroups = useFormStore((s) => s.data.affiliatedGroups);
  const ownershipAssignments = useFormStore((s) => s.data.ownershipAssignments);
  const companyLegalName = useFormStore((s) => s.data.companyLegalName);
  const federalTaxId = useFormStore((s) => s.data.federalTaxId);
  const primaryEligibleEmployees = useFormStore((s) => s.data.primaryEligibleEmployees);
  const setField = useFormStore((s) => s.setField);
  const addOwner = useFormStore((s) => s.addOwner);
  const updateOwner = useFormStore((s) => s.updateOwner);
  const addAffiliatedGroup = useFormStore((s) => s.addAffiliatedGroup);
  const addOwnershipAssignment = useFormStore((s) => s.addOwnershipAssignment);
  const {
    guardedRemoveOwner,
    guardedUpdateOwnershipAssignment,
    guardedRemoveOwnershipAssignment,
    guardedUpdateAffiliatedGroup,
    guardedRemoveAffiliatedGroup,
  } = useDocumentGuard();

  const clearOnNo = (value: string, ...fields: (keyof IntakeFormData)[]) => {
    if (value === "No") {
      fields.forEach((f) => setField(f, "" as IntakeFormData[typeof f]));
      validation.clearErrors(fields as string[]);
    }
  };

  const isCommonlyControlledMultiEmployerPath =
    isMultiEmployer === "Yes" && multiEmployerType === COMMONLY_CONTROLLED;
  const isCommonlyControlledLockedPath =
    isMultiEmployer === "No" && isCommonlyControlledGroup === "Yes";
  const affiliatedShown = isCommonlyControlledMultiEmployerPath || isCommonlyControlledLockedPath;

  // Ownership Breakdown company list: primary always first, then covered
  // affiliated entities (only relevant on the multi-employer + Commonly
  // Controlled path — the No + isCommonlyControlledGroup path locks Covered to No).
  const ownershipCompanies = useMemo(() => {
    const list: { id: string; name: string; entityNumber: number }[] = [
      { id: PRIMARY_ENTITY_ID, name: companyLegalName, entityNumber: 1 },
    ];
    if (isCommonlyControlledMultiEmployerPath) {
      affiliatedGroups.forEach((g, i) => {
        if (g.covered === true) list.push({ id: g.id, name: g.name, entityNumber: i + 2 });
      });
    }
    return list;
  }, [affiliatedGroups, companyLegalName, isCommonlyControlledMultiEmployerPath]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-navy mb-1">Company Structure</h1>
      <p className="text-gray-500 mb-6">Ownership and organization details</p>

      {/* Section 1: Collective Bargaining */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <Megaphone size={20} className="text-gray-500" />
          Collective Bargaining
        </h2>

        <YesNoInput field="hasCba" label="Will the plan cover employees subject to a CBA?" validation={validation} required onChange={(v) => clearOnNo(v, "cbaUnitName", "cbaEffectiveDate")} />

        {hasCba === "Yes" && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-start section-reveal">
            <TextInput
              field="cbaUnitName"
              label="Name of Collective Bargaining Unit"
              validation={validation}
              placeholder="Unit name"
              maxLength={50}
              required
            />
            <DateInput
              field="cbaEffectiveDate"
              label="CBA Effective Date"
              validation={validation}
              required
              minDate={CBA_MIN_DATE}
              maxDate={cbaMaxDate}
            />
          </div>
        )}
      </div>

      {/* Section 2: Group Organization */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <Building2 size={20} className="text-gray-500" />
          Group Organization
        </h2>

        <YesNoInput field="hasPeo" label="Part of a PEO or Employee Leasing Company?" validation={validation} required onChange={(v) => clearOnNo(v, "peoType")} />

        {hasPeo === "Yes" && (
          <div className="mt-4 mb-4 section-reveal">
            <SelectInput
              field="peoType"
              label="Which best describes your situation?"
              validation={validation}
              options={PEO_OPTIONS}
              required
            />
            {isUnsupportedPeoMasterPlan({ hasPeo, peoType }) && (
              <Alert
                type="error"
                message="Optimyl Benefits does not underwrite PEO master plans. If you believe this was selected in error, please update your selection above. Otherwise, this application cannot proceed."
                className="mt-3"
              />
            )}
          </div>
        )}

        <hr className="my-5 border-gray-200" />

        <YesNoInput field="isMultiEmployer" label="Does your plan cover employees of more than one employer?" validation={validation} required onChange={(v) => {
          if (v === "Yes") {
            // Coming from the No path — drop the No-side answer so it doesn't
            // linger in state (and satisfy validation) after the branch flips.
            setField("isCommonlyControlledGroup", "");
          }
          if (v === "No") {
            setField("multiEmployerType", "");
          }
        }} />

        {isMultiEmployer === "Yes" && (
          <div className="mt-4 section-reveal">
            <SelectInput
              field="multiEmployerType"
              label="Which best describes your plan?"
              validation={validation}
              options={MULTI_EMPLOYER_OPTIONS}
              required
            />

            {isUnsupportedMultiEmployer({ isMultiEmployer, multiEmployerType }) && (
              <Alert
                type="error"
                message={MULTI_EMPLOYER_UNSUPPORTED_MSG}
                className="mt-3"
              />
            )}
          </div>
        )}

        {isMultiEmployer === "No" && (
          <div className="mt-4 section-reveal">
            <YesNoInput
              field="isCommonlyControlledGroup"
              label="Is your business a part of a commonly controlled group?"
              validation={validation}
              required
            />
          </div>
        )}

        {affiliatedShown && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Commonly controlled / affiliated groups
            </label>

            <PrimaryEntityCard
              companyLegalName={companyLegalName}
              federalTaxId={federalTaxId}
              eligibleEmployees={primaryEligibleEmployees}
              onEligibleEmployeesChange={(v) => setField("primaryEligibleEmployees", v)}
              touchAll={rowTouchAll}
            />

            {affiliatedGroups.map((group, i) => (
              <AffiliatedGroupRow
                key={group.id}
                index={i}
                group={group}
                onUpdate={(data) => guardedUpdateAffiliatedGroup(i, { ...group, ...data })}
                onRemove={() => guardedRemoveAffiliatedGroup(i)}
                touchAll={rowTouchAll}
                coveredLocked={isCommonlyControlledLockedPath}
                canRemove={affiliatedGroups.length > 1}
              />
            ))}

            {affiliatedGroups.length < MAX_AFFILIATED_ENTITIES && (
              <button
                type="button"
                onClick={() =>
                  addAffiliatedGroup({
                    ...EMPTY_GROUP,
                    id: uuid(),
                    covered: isCommonlyControlledLockedPath ? false : null,
                  })
                }
                className={addButtonClass}
              >
                <Plus size={14} />
                Add Another Entity
              </button>
            )}

            {/* At least one non-primary entity must have Covered = Yes when the
                user can choose (multi-employer + Commonly Controlled path only —
                the locked path forces every affiliate to No by design). Shown
                once at the bottom rather than per-row so a mixed list with
                several Nos doesn't repeat the same message under each row. */}
            {isCommonlyControlledMultiEmployerPath
              && affiliatedGroups.length > 0
              && !affiliatedGroups.some((g) => g.covered === true) && (
                <Alert
                  type="error"
                  className="mt-3"
                  message="It is required for at least one entity other than the primary entity to be covered under the Optimyl policy."
                />
              )}
          </div>
        )}
      </div>

      {/* Section 3: Company Owners roster */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <User size={20} className="text-gray-500" />
          Company Owners
          {ownershipExemptStep && <OptionalForOrgTypePill orgType={stepOrgType} />}
        </h2>

        <Alert
          type="info"
          message="Add each person who holds ownership in any company that is going to be covered under the Optimyl Policy. You'll assign their percentages per company below."
          className="mb-4"
        />

        {owners.map((owner, i) => (
          <OwnerRosterRow
            key={owner.id}
            index={i}
            owner={owner}
            onUpdate={(data) => updateOwner(i, { ...owner, ...data })}
            onRemove={() => guardedRemoveOwner(i)}
            canRemove={owners.length > 1 || ownershipExemptStep}
            touchAll={rowTouchAll}
          />
        ))}

        {owners.length < MAX_OWNERS ? (
          <button
            type="button"
            onClick={() => addOwner({ ...EMPTY_OWNER, id: uuid() })}
            className={addButtonClass}
          >
            <Plus size={14} />
            Add Company Owner
          </button>
        ) : (
          <button type="button" disabled className={disabledAddButtonClass}>
            Maximum of {MAX_OWNERS} owners reached
          </button>
        )}

        {(() => {
          // Warn about roster entries not referenced by any Ownership Breakdown row —
          // those owners are silently dropped on submit (no ghost Ownership_Information__c
          // records get written for them), so surface the state before the user submits.
          // Card-level summary rather than a per-row badge to keep AC #3's "no per-owner
          // company-count label" intact. Warning only — does not block submit.
          //
          // Filter assignments to those pointing at *currently visible* companies before
          // building the assigned-owner set — otherwise a ghost assignment (row whose
          // entityId no longer exists on the form after the user flipped off the
          // multi-employer path) would falsely count its owner as assigned.
          const visibleEntityIds = new Set(ownershipCompanies.map((c) => c.id));
          const activeAssignments = ownershipAssignments.filter((a) => visibleEntityIds.has(a.entityId));
          const assignedOwnerIds = new Set(activeAssignments.map((a) => a.ownerId).filter(Boolean));
          const unassigned = owners.filter((o) => o.firstName.trim() || o.lastName.trim())
            .filter((o) => !assignedOwnerIds.has(o.id));
          if (unassigned.length === 0) return null;
          const label = unassigned.length === 1 ? "1 owner isn't" : `${unassigned.length} owners aren't`;
          return (
            <Alert
              type="warning"
              className="mt-4"
              message={<>{label} listed in any company's ownership below. Assign them or remove them from the roster.</>}
            />
          );
        })()}

      </div>

      {/* Section 4: Ownership Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <PieChart size={20} className="text-gray-500" />
          Ownership Breakdown
          {ownershipExemptStep && <OptionalForOrgTypePill orgType={stepOrgType} />}
        </h2>

        <Alert
          type="info"
          message="Each covered company must total 100% ownership. Owners with less than 10% ownership must be receiving W-2 wages to be eligible for benefits."
          className="mb-4"
        />

        {(() => {
          // Precompute the set of ownerIds whose enrollingInBenefits=true assignments
          // span more than one currently-visible entity — each affected row flags itself
          // with the AC error copy so the user can see exactly which assignments to fix.
          const visibleEntityIds = new Set(ownershipCompanies.map((c) => c.id));
          const enrollingCountsByOwner = new Map<string, number>();
          for (const a of ownershipAssignments) {
            if (a.enrollingInBenefits !== true) continue;
            if (!a.ownerId || !visibleEntityIds.has(a.entityId)) continue;
            enrollingCountsByOwner.set(a.ownerId, (enrollingCountsByOwner.get(a.ownerId) ?? 0) + 1);
          }
          const multiEnrollOwnerIds = new Set<string>(
            Array.from(enrollingCountsByOwner.entries()).filter(([, c]) => c > 1).map(([id]) => id),
          );
          return ownershipCompanies.map((company) => {
            const rows = ownershipAssignments.filter((a) => a.entityId === company.id);
            return (
              <OwnershipSection
                key={company.id}
                entityId={company.id}
                companyName={company.name}
                entityNumber={company.entityNumber}
                assignments={rows}
                owners={owners}
                onAddRow={addOwnershipAssignment}
                onUpdateRow={guardedUpdateOwnershipAssignment}
                onRemoveRow={guardedRemoveOwnershipAssignment}
                touchAll={rowTouchAll}
                multiEnrollOwnerIds={multiEnrollOwnerIds}
              />
            );
          });
        })()}
      </div>
    </div>
  );
}
